import { Constants } from "config/constants";
import { object, string, number, mixed, lazy } from "yup";

/**
 * This returns an object composed of conditional `yup` schema and initial values
 * related to residential details form.
 *
 * @param {object} validationParams - Object containing `idType` and `profileType` properties of the official.
 * @param {string} validationParams.idType - The idType of the current official e.g `"Kenyan Citizen"`.
 * @param {string} validationParams.isCoApplicant - The isCoApplicant  e.g `"Co-Applicant"`.
 * @return {object} This returns an object containing schema and initial values based on schema.
 * @example
 *  {
 *    schema
 *    schemaInitialValues
 *  }
 */
const residentialDetailsValidation = ({ idType, isCoApplicant }) => {
  let postalAddressSchema = object().shape({});
  if (!isCoApplicant) {
    postalAddressSchema = object().shape({
      postalAddress: lazy((value) => {
        if (!value)
          return mixed().when("$makeFieldsOptional", {
            is: (makeFieldsOptional) => makeFieldsOptional,
            then: mixed().optional(),
            otherwise: mixed().required("Postal address is required"),
          });

        const typeofField = [
          Constants.ID_TYPES.KENYAN_CITIZEN,
          Constants.ID_TYPES.FOREIGN_RESIDENT,
        ].includes(idType)
          ? "number"
          : "string";

        switch (typeofField) {
          case "number":
            return number("Enter valid postal address")
              .typeError("Postal address must be a number")
              .strict(true)
              .when("$makeFieldsOptional", {
                is: (makeFieldsOptional) => makeFieldsOptional,
                then: number().optional(),
                otherwise: number().required("Postal address is required"),
              });
          case "string":
            return string()
              .trim("Postal address cannot include leading and trailing spaces")
              .strict(true)
              .when("$makeFieldsOptional", {
                is: (makeFieldsOptional) => makeFieldsOptional,
                then: string().optional(),
                otherwise: string().required("Postal address is required"),
              });
        }
      }),
      serviceAddress: string()
        .trim("Service address cannot include leading and trailing spaces")
        .strict(true)
        .optional(),
    });
  }
  const electronicAddressSchema = object().shape({
    electronicAddress: object().shape({
      emailAddress: string()
        .email("Invalid email format")
        .trim("Email cannot include leading and trailing spaces")
        .strict(true)
        .when("$makeFieldsOptional", {
          is: (makeFieldsOptional) => makeFieldsOptional,
          then: string().optional(),
          otherwise: string().required("Email address is required"),
        }),
      mobileNumber: string()
        .trim("Mobile number cannot include leading and trailing spaces")
        .when("$makeFieldsOptional", {
          is: (makeFieldsOptional) => makeFieldsOptional,
          then: string().optional(),
          otherwise: string().required("Mobile number is required"),
        })
        .matches(Constants.PHONE_NUMBER_REGEX, "Phone number is not valid")
        .test("length", function (value) {
          if (
            value &&
            [
              Constants.ID_TYPES.KENYAN_CITIZEN,
              Constants.ID_TYPES.FOREIGN_RESIDENT,
              Constants.ID_TYPES.LOCAL_COMPANY,
              Constants.ID_TYPES.OTHER_PARASTATALS,
            ].includes(idType)
          ) {
            if (value.length !== 13) {
              return this.createError({
                message: "Mobile number must be exactly 13 characters",
              });
            }
            if (!value.startsWith("+254")) {
              return this.createError({
                message: "Correct format is +254#########",
              });
            }
            return true;
          } else if (
            value &&
            (value.length < 11 || value.length > 13) &&
            ["Foreigner", "Foreign Company"].includes(idType)
          ) {
            return this.createError({
              message: "Mobile number is not correct",
            });
          } else return true;
        }),
    }),
  });

  const schema = object().shape({
    ...postalAddressSchema.fields,
    ...electronicAddressSchema.fields,
  });

  return {
    schema,
    schemaInitialValues: schema.cast(),
  };
};

export default residentialDetailsValidation;
